import type { CouponRejectReason } from "@/lib/coupons/engine"
import type { CouponRequestLines } from "@/lib/coupons/requestLines"
import type { AppliedCoupon } from "@/hooks/useCart"

// Cliente ÚNICO de POST /api/coupons/apply. Lo comparten los DOS caminos del
// POS: el modal donde teclea el cajero (CouponModal) y la entrada en la
// pantalla del cliente (POSScreen ← evento `coupon_code_entered`).
//
// Vive aquí y no duplicado en cada uno porque el body es money-path: el ancla
// que crea esta llamada es la que /api/orders consume al cobrar. Dos copias del
// mismo body derivan (ya pasó con otros payloads del repo), y la que se queda
// atrás falla de la peor forma — el cajero lee "cupón inválido" cuando el cupón
// estaba perfecto.

/** El zod de la ruta exige E.164 estricto; ver nota en el body. */
const E164_RE = /^\+\d{10,15}$/

/** Motivos que NO vienen del motor: son de transporte/sesión, no del cupón. */
export type CouponApplyTransportReason =
  | "unauthorized"
  | "rate_limited"
  | "server_error"
  | "network"

export type CouponApplyResult =
  | {
      ok: true
      validationId: string
      code: string
      discount_type: "percent" | "fixed"
      discount_value: number
      discountAmount: number
      /** A1b (TABC): alcohol del LOCAL (ítems y opciones). Solo presentes si la
       *  ruta los mandó NO vacíos: con 0 marcados el resultado es el de main. */
      alcoholItemIds?: string[]
      alcoholOptionIds?: string[]
    }
  | {
      ok: false
      reason: CouponRejectReason | CouponApplyTransportReason | string
      minSubtotal?: number
      /** A1b: base SIN alcohol contra la que el servidor midió el mínimo. Solo
       *  presente si excluyó algo (`min_subtotal` con alcohol en el carrito). */
      measuredSubtotal?: number
    }

export interface CouponApplyInput {
  locationId: string
  /** X-POS-Auth de la sesión — la ruta lo exige. Cajero: token de la caja
   *  (tier operations/admin). Kiosko: su capability token propio, tier `anon`
   *  con cap `order:create` (mintKioskToken) — la ruta lo acepta igual. */
  posToken: string
  /** Código YA normalizado (^[A-Z0-9]{4,20}$). */
  code: string
  /** Subtotal de ítems ANTES de descuentos: el mismo número contra el que el
   *  server compara `min_subtotal`. NO el subtotal post-descuento. */
  subtotal: number
  /** Quién llama — el body de la ruta, los CHECK de coupon_validations/
   *  coupon_redemptions y record_sale ya distinguen 'pos' de 'kiosk'. */
  channel: "pos" | "kiosk"
  terminalId?: string | null
  employeeId?: string | null
  /** E.164 (+1XXXXXXXXXX) si el cajero ya capturó al cliente. */
  customerPhone?: string | null
  /** A1b (TABC): líneas del carrito (couponRequestLines) para que la ruta mida
   *  la base del cupón sin alcohol. Ausente = body de main. */
  lines?: CouponRequestLines
}

export async function requestCouponApply(input: CouponApplyInput): Promise<CouponApplyResult> {
  try {
    const res = await fetch("/api/coupons/apply", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-POS-Auth": input.posToken },
      body: JSON.stringify({
        locationId: input.locationId,
        code: input.code,
        subtotal: input.subtotal,
        channel: input.channel,
        terminalId: input.terminalId ?? null,
        employeeId: input.employeeId ?? null,
        // El zod del server exige E.164 estricto: mandar un teléfono con otra
        // forma tumba el request entero con 400 y el cajero leería "cupón
        // inválido" por culpa del TELÉFONO, no del cupón. Se omite si no calza.
        ...(input.customerPhone && E164_RE.test(input.customerPhone)
          ? { customerPhone: input.customerPhone }
          : {}),
        ...(input.lines ? { lines: input.lines } : {}),
      }),
    })

    if (res.status === 401) return { ok: false, reason: "unauthorized" }
    if (res.status === 429) return { ok: false, reason: "rate_limited" }

    const data = (await res.json().catch(() => null)) as
      | { ok?: boolean; reason?: unknown; minSubtotal?: unknown; measuredSubtotal?: unknown; validationId?: unknown; coupon?: unknown; discountAmount?: unknown; alcoholItemIds?: unknown; alcoholOptionIds?: unknown }
      | null

    if (!data) return { ok: false, reason: "server_error" }

    if (data.ok !== true) {
      // 500 (fallo de escritura del ancla) llega sin `reason` → genérico.
      return {
        ok: false,
        reason: typeof data.reason === "string" && data.reason ? data.reason : "server_error",
        minSubtotal: typeof data.minSubtotal === "number" ? data.minSubtotal : undefined,
        ...(typeof data.measuredSubtotal === "number" ? { measuredSubtotal: data.measuredSubtotal } : {}),
      }
    }

    const coupon = data.coupon as
      | { code?: unknown; discount_type?: unknown; discount_value?: unknown }
      | undefined
    if (
      typeof data.validationId !== "string" ||
      typeof coupon?.code !== "string" ||
      (coupon.discount_type !== "percent" && coupon.discount_type !== "fixed") ||
      typeof coupon.discount_value !== "number" ||
      typeof data.discountAmount !== "number"
    ) {
      // 200 con forma inesperada: tratarlo como éxito dejaría un `validationId`
      // basura en el store y la orden se caería recién AL COBRAR.
      return { ok: false, reason: "server_error" }
    }

    return {
      ok: true,
      validationId: data.validationId,
      code: coupon.code,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      discountAmount: data.discountAmount,
      ...couponAlcoholListsFromResponse(data),
    }
  } catch {
    return { ok: false, reason: "network" }
  }
}

/**
 * Las listas de alcohol que devuelve la ruta, SOLO si alguna trae algo. Una
 * lista ausente o con forma rota cuenta como [] (no tumba un éxito: el cobro en
 * /api/orders excluye igual). Exportada para el preview del micrositio.
 */
export function couponAlcoholListsFromResponse(data: {
  alcoholItemIds?: unknown
  alcoholOptionIds?: unknown
}): { alcoholItemIds?: string[]; alcoholOptionIds?: string[] } {
  const strings = (v: unknown): string[] =>
    Array.isArray(v) && v.every((x) => typeof x === "string") ? [...v] : []
  const alcoholItemIds = strings(data.alcoholItemIds)
  const alcoholOptionIds = strings(data.alcoholOptionIds)
  if (alcoholItemIds.length === 0 && alcoholOptionIds.length === 0) return {}
  return { alcoholItemIds, alcoholOptionIds }
}

/**
 * El cupón del carrito a partir de un apply exitoso. ÚNICO armado: lo usan el
 * CouponModal del cajero, la pantalla del cliente (POSScreen), KioskCouponScreen
 * y el premio del juego del kiosko (KioskShell). Sin listas de alcohol devuelve
 * exactamente el objeto que esos cuatro armaban a mano en main.
 */
export function appliedCouponFromApplyResult(
  result: Extract<CouponApplyResult, { ok: true }>,
): AppliedCoupon {
  return {
    validationId: result.validationId,
    code: result.code,
    discount_type: result.discount_type,
    discount_value: result.discount_value,
    discountAmount: result.discountAmount,
    ...(result.alcoholItemIds || result.alcoholOptionIds
      ? { alcoholItemIds: result.alcoholItemIds ?? [], alcoholOptionIds: result.alcoholOptionIds ?? [] }
      : {}),
  }
}
